/**
 * TabbedLeftPanel - Panel izquierdo con sistema de pestañas
 *
 * @version 1.3
 * @updated 2026-02-20
 * @module core/layouts/panels
 *
 * @description
 * Sistema de pestañas para el LeftPanel.
 * La pestaña "Planificación" fue eliminada del sidebar — su entrada
 * es el botón "Planificar" del ModeSelector en el TopBar.
 *
 * @remarks
 * Consume LeftPanelContext para acceder a collapsed y onToggle sin prop drilling.
 * Cambia automáticamente al tab correspondiente cuando se selecciona un elemento.
 */

import { useState, useEffect } from 'react';
import {
  TreeDeciduous, Trees, Umbrella, Droplets, Waves, Droplet,
} from 'lucide-react';
import { TreesPanel } from './TreesPanel';
import { LeftPanelToggle } from './LeftPanel';
import { useMapTools } from '../../../map-tools';
import './tabbed-left-panel.css';

type TabType =
  | 'trees'
  | 'parks'
  | 'sunshades'
  | 'fountains'
  | 'water-courses'
  | 'water-mirrors';

interface TabInfo {
  id: TabType;
  icon: React.ReactNode;
  tooltip: string;
  enabled: boolean;
}

/**
 * Pestañas del panel izquierdo.
 * Los íconos ambientales coinciden con ENV_COLUMNS en CellsSection.tsx
 * y los botones del módulo ambiental en BottomToolbar.tsx.
 */
const TABS: TabInfo[] = [
  {
    id: 'trees',
    icon: <TreeDeciduous size={16} />,
    tooltip: 'Árboles',
    enabled: true,
  },
  {
    id: 'parks',
    icon: <Trees size={16} />,
    tooltip: 'Parques y plazas',
    enabled: true,
  },
  {
    id: 'sunshades',
    icon: <Umbrella size={16} />,
    tooltip: 'Parasoles',
    enabled: true,
  },
  {
    id: 'fountains',
    icon: <Droplets size={16} />,
    tooltip: 'Fuentes y bebederos',
    enabled: true,
  },
  {
    id: 'water-courses',
    icon: <Waves size={16} />,
    tooltip: 'Cursos de agua',
    enabled: true,
  },
  {
    id: 'water-mirrors',
    icon: <Droplet size={16} />,
    tooltip: 'Espejos de agua',
    enabled: true,
  },
];

export function TabbedLeftPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabType>('trees');
  const { selectedTreeId } = useMapTools();

  /**
   * Cambiar automáticamente al tab correspondiente cuando se selecciona un elemento
   */
  useEffect(() => {
    if (selectedTreeId) {
      console.log('[TabbedLeftPanel] Árbol seleccionado, cambiando a tab trees');
      setActiveTab('trees');
    }
  }, [selectedTreeId]);

  const renderTabContent = (): React.ReactNode => {
    switch (activeTab) {
      case 'trees':
        return <TreesPanel />;
      case 'parks':
        return <div className="tab-placeholder"><p>Parques y plazas — próximamente</p></div>;
      case 'sunshades':
        return <div className="tab-placeholder"><p>Parasoles — próximamente</p></div>;
      case 'fountains':
        return <div className="tab-placeholder"><p>Fuentes y bebederos — próximamente</p></div>;
      case 'water-courses':
        return <div className="tab-placeholder"><p>Cursos de agua — próximamente</p></div>;
      case 'water-mirrors':
        return <div className="tab-placeholder"><p>Espejos de agua — próximamente</p></div>;
      default:
        return null;
    }
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${
              !tab.enabled ? 'disabled' : ''
            }`}
            onClick={() => tab.enabled && setActiveTab(tab.id)}
            disabled={!tab.enabled}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon || <div className="tab-placeholder-icon" />}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            {renderTabContent()}
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
